import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Gift, XCircle, RotateCcw, Heart } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Badge } from "./saUi";

const PLANS = [{ v: "3m", l: "3 mnd" }, { v: "6m", l: "6 mnd" }, { v: "12m", l: "12 mnd" }];

export default function Subscriptions() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [plan, setPlan] = useState({});

  const load = () => api.get("/admin/subscriptions").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (uid, fn, ok) => {
    setBusy(uid);
    try { await fn(); toast.success(ok); await load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const grant = (uid) => act(uid, () => api.post(`/admin/users/${uid}/grant-license`, { plan: plan[uid] || "3m" }), "Licentie toegekend");
  const revoke = (uid) => act(uid, () => api.post(`/admin/users/${uid}/revoke-license`), "Licentie ingetrokken");
  const reset = (uid) => act(uid, () => api.post(`/admin/users/${uid}/reset-trial`), "Proefperiode gereset");
  const toggleFF = (uid, on) => act(uid, () => api.post(`/admin/users/${uid}/friends-family`, { enabled: on }), on ? "Friends & Family toegekend 💛" : "Friends & Family ingetrokken");

  if (!rows) return <div className="flex items-center gap-2 text-slate-400 py-12"><Loader2 className="w-5 h-5 animate-spin" /> Laden…</div>;
  const managed = rows.filter((r) => r.role !== "super_admin" && r.status !== "beheer");

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-2 overflow-x-auto" data-testid="sa-subscriptions">
      <table className="w-full text-sm min-w-[720px]">
        <thead>
          <tr className="text-left text-slate-400 text-xs uppercase tracking-wide border-b border-slate-200">
            <th className="py-3 px-2">Gebruiker</th><th className="px-2">Status</th><th className="px-2">Licentie</th>
            <th className="px-2">Verloopt / proef eindigt</th><th className="px-2 text-right">Acties</th>
          </tr>
        </thead>
        <tbody>
          {managed.map((r) => (
            <tr key={r.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sa-sub-row-${r.user_id}`}>
              <td className="py-3 px-2">
                <p className="font-bold">{r.name}</p>
                <p className="text-xs text-slate-400">{r.email || "geen e-mail"}</p>
              </td>
              <td className="px-2"><Badge status={r.status} /></td>
              <td className="px-2 text-slate-600">{r.license_type ? r.license_type : "—"}</td>
              <td className="px-2 text-slate-500 text-xs">
                {r.license_expires ? new Date(r.license_expires).toLocaleDateString("nl-NL") :
                 r.trial_ends ? `proef t/m ${new Date(r.trial_ends).toLocaleDateString("nl-NL")}` : "—"}
                {r.days_left != null && <span className="ml-1 text-slate-400">({r.days_left}d)</span>}
              </td>
              <td className="px-2">
                <div className="flex items-center justify-end gap-1.5 flex-wrap">
                  <select value={plan[r.user_id] || "3m"} onChange={(e) => setPlan({ ...plan, [r.user_id]: e.target.value })}
                    data-testid={`sa-sub-plan-${r.user_id}`}
                    className="bg-white border border-slate-300 rounded-md px-2 py-1 text-xs outline-none">
                    {PLANS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
                  </select>
                  <button onClick={() => grant(r.user_id)} disabled={busy === r.user_id} data-testid={`sa-sub-grant-${r.user_id}`}
                    className="flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 rounded-md px-2 py-1 text-xs font-bold disabled:opacity-50">
                    <Gift className="w-3.5 h-3.5" /> Toekennen
                  </button>
                  <button onClick={() => revoke(r.user_id)} disabled={busy === r.user_id} data-testid={`sa-sub-revoke-${r.user_id}`}
                    className="flex items-center gap-1 bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 rounded-md px-2 py-1 text-xs font-bold disabled:opacity-50">
                    <XCircle className="w-3.5 h-3.5" /> Intrekken
                  </button>
                  <button onClick={() => reset(r.user_id)} disabled={busy === r.user_id} data-testid={`sa-sub-reset-${r.user_id}`}
                    className="flex items-center gap-1 bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 rounded-md px-2 py-1 text-xs font-bold disabled:opacity-50">
                    <RotateCcw className="w-3.5 h-3.5" /> Proef
                  </button>
                  <button onClick={() => toggleFF(r.user_id, !r.ff_account)} disabled={busy === r.user_id} data-testid={`sa-sub-ff-${r.user_id}`}
                    className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold border disabled:opacity-50 ${r.ff_account ? "bg-pink-500 text-white border-pink-500 hover:bg-pink-600" : "bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100"}`}>
                    <Heart className={`w-3.5 h-3.5 ${r.ff_account ? "fill-white" : ""}`} /> {r.ff_account ? "F&F aan" : "F&F"}
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {managed.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">Geen gebruikers met abonnement.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
